import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';
import { StepType } from '../types';

interface StepHelpPopoverProps {
  stepType: StepType;
  themeColor: 'cyan' | 'blue' | 'purple' | 'yellow' | 'pink';
  children: React.ReactNode;
}

const VISITED_STEPS_KEY = 'boyangcong_visited_steps';

// Get color classes based on theme
const getColorClasses = (color: StepHelpPopoverProps['themeColor']) => {
  const colorMap = {
    cyan: {
      iconColor: 'text-cyan-400 hover:text-cyan-300',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      titleColor: 'text-cyan-300',
    },
    blue: {
      iconColor: 'text-blue-400 hover:text-blue-300',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      titleColor: 'text-blue-300',
    },
    purple: {
      iconColor: 'text-purple-400 hover:text-purple-300',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      titleColor: 'text-purple-300',
    },
    yellow: {
      iconColor: 'text-yellow-400 hover:text-yellow-300',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      titleColor: 'text-yellow-300',
    },
    pink: {
      iconColor: 'text-pink-400 hover:text-pink-300',
      bgColor: 'bg-pink-500/10',
      borderColor: 'border-pink-500/30',
      titleColor: 'text-pink-300',
    },
  };
  return colorMap[color];
};

export default function StepHelpPopover({ 
  stepType, 
  themeColor, 
  children 
}: StepHelpPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const colors = getColorClasses(themeColor);

  // Check if this step has been visited before
  useEffect(() => {
    const visitedSteps = sessionStorage.getItem(VISITED_STEPS_KEY);
    const visited = visitedSteps ? JSON.parse(visitedSteps) : [];
    
    if (!visited.includes(stepType)) {
      // First time visiting this step - auto open
      setIsOpen(true);
      
      // Mark as visited
      sessionStorage.setItem(
        VISITED_STEPS_KEY, 
        JSON.stringify([...visited, stepType])
      );
      
      // Auto close after 3 seconds
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [stepType]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current && 
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close when step changes
  useEffect(() => {
    return () => {
      setIsOpen(false);
    };
  }, [stepType]);

  return (
    <div className="relative inline-flex">
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1.5 rounded-full transition-colors ${colors.iconColor} ${
          isOpen ? colors.bgColor : 'hover:bg-gray-700/50'
        }`}
        title="查看帮助"
      >
        <HelpCircle className="w-5 h-5" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={popoverRef}
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`
              absolute top-full left-0 mt-2 z-50
              w-80 p-4 rounded-lg
              ${colors.bgColor} border ${colors.borderColor}
              shadow-xl backdrop-blur-sm
            `}
          >
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-300 hover:bg-gray-700/50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Content */}
            <div className="text-sm text-gray-400 pr-6">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

